import {
  ExternalHyperlink,
  ImageRun,
  Paragraph,
  Table,
  TableCell,
  TableLayoutType,
  TableRow,
  VerticalAlign,
  WidthType,
} from 'docx';

// Utils
import {
  hasValidCertification,
  isValidCertification,
  formatDayMonthYearLabel,
} from '@shared/utils';
import { convertPtToPx } from '@/components/template/resume/utils/converters';
import {
  bodyRun,
  dividerParagraph,
  iconImageRun,
  sidebarSectionTitle,
} from '../../utils';

// Types
import { TemplateProps } from '@/components/template/resume/type';

// Components
import {
  type IconBuffers,
  SOCIAL_ICON_SVGS,
} from '@/components/template/resume/utils/icons';
import { svgToImageBuffer } from '@/components/template/resume/utils/image';

// Tokens
import { SECTION_TITLES } from '@/components/template/resume/Default/tokens';

// Constants
import { FONT_SIZES, NO_BORDER, COLORS, SPACINGS } from '../../constants';

/** Cert icon generation size in px for sharp rendering */
export const CERT_ICON_GEN_SIZE = convertPtToPx(28);

/** Small inline link icon size at 10pt */
const CERT_LINK_ICON_SIZE = convertPtToPx(10);

// Icon column width in DXA: 20pt icon + padding
const CERT_ICON_COL_DXA = 600;

export const CertificatesSection = (
  certs: TemplateProps['data']['certs'],
  certIcons: IconBuffers,
  linkIconBuffer: Uint8Array | null,
  tableWidth: number,
): (Paragraph | Table)[] => {
  if (!certs || !hasValidCertification(certs)) return [];

  const validCerts = certs.filter(isValidCertification);
  if (!validCerts.length) return [];

  const contentColDxa = tableWidth - CERT_ICON_COL_DXA;

  const blocks: (Paragraph | Table)[] = [
    dividerParagraph({ before: 0, after: SPACINGS.sectionDividerGap }),
    sidebarSectionTitle(SECTION_TITLES.certifications, 0),
  ];

  for (const cert of validCerts) {
    const { name = '', issue = '', url = '', org = '' } = cert ?? {};
    const orgIconBuffer = certIcons.lookup(org);
    const iconRun = iconImageRun(orgIconBuffer, convertPtToPx(20));

    const contentParagraphs: Paragraph[] = [];

    if (name) {
      contentParagraphs.push(
        new Paragraph({
          children: [bodyRun(name, { bold: true })],
        }),
      );
    }

    // Meta row: issued date + optional link icon
    const metaChildren: (
      | ExternalHyperlink
      | ImageRun
      | ReturnType<typeof bodyRun>
    )[] = [];

    if (issue) {
      metaChildren.push(
        bodyRun(`Issued ${formatDayMonthYearLabel(issue)}`, {
          size: FONT_SIZES.base,
          color: COLORS.primary,
        }),
      );
    }

    if (url) {
      if (metaChildren.length) metaChildren.push(bodyRun('  '));
      metaChildren.push(
        new ExternalHyperlink({
          link: url,
          children: [
            linkIconBuffer
              ? new ImageRun({
                  data: linkIconBuffer,
                  transformation: {
                    width: CERT_LINK_ICON_SIZE,
                    height: CERT_LINK_ICON_SIZE,
                  },
                  type: 'png',
                  altText: {
                    title: 'Link',
                    description: 'Certification link',
                    name: 'link-icon',
                  },
                })
              : bodyRun('↗', { size: FONT_SIZES.base, color: COLORS.primary }),
          ],
        }),
      );
    }

    if (metaChildren.length) {
      contentParagraphs.push(
        new Paragraph({
          spacing: { before: SPACINGS.small },
          children: metaChildren,
        }),
      );
    }

    if (!contentParagraphs.length) continue;

    blocks.push(
      new Table({
        layout: TableLayoutType.FIXED,
        width: { size: tableWidth, type: WidthType.DXA },
        columnWidths: [CERT_ICON_COL_DXA, contentColDxa],
        borders: NO_BORDER,
        rows: [
          new TableRow({
            children: [
              // Icon cell
              new TableCell({
                width: { size: CERT_ICON_COL_DXA, type: WidthType.DXA },
                borders: NO_BORDER,
                verticalAlign: VerticalAlign.TOP,
                margins: { top: SPACINGS.large },
                children: [
                  new Paragraph({ children: iconRun ? [iconRun] : [] }),
                ],
              }),
              // Content cell
              new TableCell({
                width: { size: contentColDxa, type: WidthType.DXA },
                borders: NO_BORDER,
                verticalAlign: VerticalAlign.TOP,
                margins: { top: SPACINGS.large },
                children: contentParagraphs,
              }),
            ],
          }),
        ],
      }),
    );
  }

  return blocks;
};

/** Preloads the link icon buffer for cert URL badges */
export const loadCertLinkIcon = (): Promise<Uint8Array | null> => {
  const defaultIcon = SOCIAL_ICON_SVGS.DEFAULT ?? SOCIAL_ICON_SVGS.LINKEDIN;
  if (!defaultIcon) return Promise.resolve(null);

  return svgToImageBuffer(
    defaultIcon,
    CERT_LINK_ICON_SIZE,
    CERT_LINK_ICON_SIZE,
  );
};
